import React, { useMemo, useEffect, useState, useCallback } from "react";
import SequenceFlowsDialog from "components/dialogs/SequenceFlowsDialog";
import { Container, Stack } from "@mui/material";
import { getTranslation } from "common";
import MyActivitiesFuncBar from "../components/MyActivitiesFuncBar";
import MyActivitiesPanels from "../components/MyActivitiesPanels";
import {
  useActivities,
  useSelectedOrganization,
  useTranslations,
  useUser,
  usePriorities,
} from "hooks";
import useUserDataKeys from "hooks/useUserDataKeys";
import { StartProcess, TaskDetailDialog } from "components/dialogs";
import {
  DragDropContext,
  OnDragEndResponder,
  OnDragUpdateResponder,
} from "react-beautiful-dnd";
import {
  CONSTANTS,
  filterTasks,
  processTasks,
  sortTasks,
} from "common/utilities";
import { organizationsApi, processesApi } from "api";
import { SequenceFlow, ServerTag, Task } from "model/activitiesModel";
import { SortState } from "hooks/useSortableList";
import { SortCriterion } from "common/utilities";
import { ProcessModelType } from "model/processModel";
import { Priority } from "hooks/usePriorities";
import useSubscriptionFeatures from "hooks/useSubscriptionFeatures";
import { useNavigate, useLocation } from "react-router-dom";
const LOCAL_STORAGE_KEY = `myActivitiesFilters`;

const MyActivities = () => {
  const navigate = useNavigate();
  const { bypassSubscription, liveProcessCanUse, refreshSubscriptionFeatures } = useSubscriptionFeatures();
  const translations = useTranslations();
  const [isInitialized, setIsInitialized] = useState(false);
  const organization = useSelectedOrganization();
  const [completeLabel, setCompleteLabel] = useState<boolean>(false);
  const [tasks, setTasks, orderedArray, loading] = useActivities(organization);
  const user = useUser();
  const [searchText, setSearchText] = useState<string>("");
  const [searchTags, setSearchTags] = useState<number[]>([]);
  const [tagList, setTagList] = useState<ServerTag[]>([]);
  const [all] = useState<boolean>(false);
  const [startProcess, setStartProcess] = useState(false);
  const [switchChecked] = useState(false);
  const [dragItem, setDragItem] = useState<Task | null>(null);
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>([]);
  const [priorities] = usePriorities(organization?.id || null);
  const [selectedModels, setSelectedModels] = useState<number[]>([]);
  const [selectedActivities, setSelectedActivities] = useState<string[]>([]);
  const [selectedUserDataKey, setSelectedUserDataKey] = useState<string | null>(
    null
  );

  const [userDataKeys] = useUserDataKeys(tasks);
  const selectedOrganization = useSelectedOrganization();
  const [processModels, setProcessModels] = useState<ProcessModelType[]>([]);
  const [sortCriteria, setSortCriteria] = useState<SortCriterion>({});
  const [showAllTags, setShowAllTags] = useState(false);
  const [showOnlyIdUserZero, setShowOnlyIdUserZero] = useState(false);
  const [openSequenceDialog, setOpenSequenceDialog] = useState(false);
  const [sequenceTask, setSequenceTask] = useState<Task | null>(null);
  const [view, setView] = useState<'minimal' | 'compact' | 'extended'>(
    () => (localStorage.getItem('taskCardView') as any) || 'extended'
  );

  const handleViewChange = (newView: 'minimal' | 'compact' | 'extended') => {
    localStorage.setItem('taskCardView', newView);
    setView(newView);
  };

  const uniqueActivities = useMemo(() => {
    const allTasks = [...tasks.todo, ...tasks.doing];
    return Array.from(
      new Set(allTasks.map((task) => task.flownodename))
    ).filter((activity): activity is string => activity !== null);
  }, [tasks.todo, tasks.doing]);

  useEffect(() => {
    if (isInitialized && !loading && userDataKeys.length > 0) {
      setSelectedUserDataKey(prev => prev && userDataKeys.includes(prev) ? prev : null);
    }
  }, [userDataKeys, loading, isInitialized])

  //ci arriva da TaskDetailDialog
  const updateTaskPriority = useCallback(
    (taskId: number, newPriority: Priority) => {
      setTasks((prevTasks) => ({
        todo: prevTasks.todo.map((task) =>
          task.idprocesstoken === taskId
            ? { ...task, priority_name: newPriority.name }
            : task
        ),
        doing: prevTasks.doing.map((task) =>
          task.idprocesstoken === taskId
            ? { ...task, priority_name: newPriority.name }
            : task
        ),
      }));
    },
    [setTasks]
  ); //può essere vuoto?

  useEffect(() => {
    setCompleteLabel(
      JSON.parse(
        localStorage.getItem(CONSTANTS.STORE_LABEL_COMPLETE_MODE) || "false"
      )
    );
  }, []);


  // PRIMA: Leggi dal localStorage
  useEffect(() => {
    const savedFilters = localStorage.getItem(
      `${LOCAL_STORAGE_KEY}-${organization?.id}`
    );
    if (savedFilters) {
      const filters = JSON.parse(savedFilters);
      setSearchText(filters.searchText || "");
      setSearchTags(filters.searchTags || []);
      setSelectedPriorities(filters.selectedPriorities || []);
      setSelectedModels(filters.selectedModels || []);
      setSelectedActivities(filters.selectedActivities || []);
      setSelectedUserDataKey(filters.selectedUserDataKey || null);
      setSortCriteria(filters.sortCriteria || {});
    }
    setIsInitialized(true); // <-- Marca come inizializzato DOPO aver letto
  }, [organization]);

  // POI: Salva nel localStorage SOLO dopo l'inizializzazione
  useEffect(() => {
    if (!isInitialized) return; // <-- Non salvare se non ancora inizializzato

    const filters = {
      searchText,
      searchTags,
      selectedPriorities,
      selectedActivities,
      selectedModels,
      selectedUserDataKey,
      sortCriteria,
    };
    localStorage.setItem(
      `${LOCAL_STORAGE_KEY}-${organization?.id}`,
      JSON.stringify(filters)
    );
  }, [
    isInitialized, // <-- Aggiungi alle dipendenze
    organization,
    sortCriteria,
    searchText,
    searchTags,
    selectedPriorities,
    selectedActivities,
    selectedModels,
    selectedUserDataKey,
  ]);

  const handleSelectUserDataKey = (selectedKey: string | null) => {
    setSelectedUserDataKey(selectedKey);
    if (!selectedKey) {
      setSortCriteria((prev) => ({
        ...prev,
        userdata: SortState.None,
      }));
    }
  };

  const SUPPORTED_SORTS = [
    {
      field: "priority",
      label: (userDataKey: string | null) =>
        getTranslation(translations, "generic.sort.by.priority"),
    },
    {
      field: "name",
      label: (userDataKey: string | null) =>
        getTranslation(translations, "generic.sort.by.process_name"),
    },
    {
      field: "startDate",
      label: (userDataKey: string | null) =>
        getTranslation(translations, "generic.sort.by.startdate"),
    },
    {
      checkDisabled: function (userDataKey: string | null) {
        return !Boolean(userDataKey);
      },
      field: "userdata",
      label: (userDataKey: string | null) =>
        `${getTranslation(translations, "generic.sort.by.userdatakey")} ${userDataKey || ""
        }`,
    },
  ];

  useEffect(() => {
    if (selectedOrganization?.id) {
      organizationsApi
        .get(`/${selectedOrganization.id}/processmodels/v2`)
        .then((res) => {
          setProcessModels(
            (res.data.processmodels as ProcessModelType[]).filter(
              (a) => a.modeltype
            )
          );
        })
        .catch((error) => { });
    }
  }, [selectedOrganization]);


  useEffect(() => {
    if (organization) {
      (async () => {
        try {
          const { data } = await organizationsApi.get<{
            tag: { name: string; color: string; id: number }[];
          }>(`${organization?.id}/tags`);
          setTagList(
            data.tag.map((tag) => ({
              color: tag.color,
              idtag: tag.id,
              tag: tag.name,
            }))
          );
        } catch {
        } finally {
        }
      })();
    }
  }, [organization, tasks]);

  const orderArray = useCallback(
    (
      source: "todo" | "doing",
      dest: "todo" | "doing",
      taskId: number,
      destIdx: number | null = null
    ) => {
      setTasks((prevTasks) => {
        const _tasks = {
          todo: [...prevTasks.todo],
          doing: [...prevTasks.doing],
        };

        const sourceIdx = _tasks[source].findIndex(
          (task) => task.idprocesstoken === taskId
        );
        if (sourceIdx === -1) {
          return _tasks;
        }

        const [task] = _tasks[source].splice(sourceIdx, 1);

        if (destIdx !== null) {
          task.state = source === "todo" ? 1 : 0;

          _tasks[dest].splice(destIdx, 0, { ...task, loading: true });
        }

        orderedArray.current = {
          todo: _tasks.todo.map((_) => _.idprocesstoken),
          doing: _tasks.doing.map((_) => _.idprocesstoken),
        };

        return _tasks;
      });
    },
    [orderedArray, setTasks]
  );


  const fromTodoTo_Doing = async (task: Task) => {
    try {
      await processesApi.post(`/${task.idprocesstoken}/nextstate/doing`);
    } catch {
    } finally {
    }
  };
  const fromDoingTo_Done = async (task: Task) => {
    try {
      await processesApi.post(`/${task.idprocesstoken}/nextstate/done`);
    } catch {
    } finally {
    }
  };
  const fromDoingTo_Todo = async (task: Task) => {
    try {
      await processesApi.post(`/${task.idprocesstoken}/undo`);
    } catch {
    } finally {
    }
  };
  const fromDoingTo_Sequence = async (
    task: Task,
    sequenceFlow: SequenceFlow
  ) => {
    try {
      await processesApi.post(
        `tokens/${task.idprocesstoken}/exit/${sequenceFlow.designerid}`
      );
    } catch {
    } finally {
    }
  };

  const filteredTags = useMemo(() => {
    return tagList.filter((tag) =>
      tasks.todo
        .concat(tasks.doing)
        .some((task) => task.jsontag?.includes(tag.idtag as unknown as string))
    );
  }, [tagList, tasks.todo, tasks.doing]);

  const handleDragUpdate: OnDragUpdateResponder = ({ destination }) => {
    if (destination && destination.droppableId === "done") {
      setDragItem((_) => {
        if (
          _?.sequenceflows &&
          _?.sequenceflows.length > 0 &&
          destination.index < _.sequenceflows.length
        ) {
          const _sequence = [..._?.sequenceflows].map((_) => ({
            ..._,
            isOver: false,
          }));
          if (_sequence[destination.index]) {
            _sequence[destination.index].isOver = true;
          }
          return {
            ..._,
            isOverDone: false,
            sequenceflows: _sequence,
          };
        }
        return !_
          ? null
          : {
            ..._,
            isOverDone:
              destination.droppableId === "done" && destination.index === 0,
          };
      });
    } else {
      setDragItem((_) => {
        if (_?.sequenceflows && _?.sequenceflows.length > 0) {
          const _sequence = [..._?.sequenceflows].map((_) => ({
            ..._,
            isOver: false,
          }));
          return {
            ..._,
            isOverDone: false,
            sequenceflows: _sequence,
          };
        }
        return !_ ? null : { ..._, isOverDone: false };
      });
    }
  };
  const handleDragEnd: OnDragEndResponder = async ({
    reason,
    source,
    destination,
    draggableId,
  }) => {
    if (destination && reason !== "CANCEL") {
      const sourceTasks = source.droppableId === "todo" ? processedToDoTasks : processedDoingTasks;
      const task = sourceTasks.find(t => t.idprocesstoken.toString() === draggableId);
      if (!task) return;

      if (source.droppableId === "doing") {
        if (destination.droppableId === "done") {
          if (task?.sequenceflows && task.sequenceflows.length > 0) {
            if (task.sequenceflows.length > destination.index) {
              if (task.sequenceflows.length > 2) {
                setSequenceTask(task);
                setOpenSequenceDialog(true);
                return;
              }
              orderArray("doing", "todo", task.idprocesstoken);
              await fromDoingTo_Sequence(
                task,
                task.sequenceflows[destination.index]
              );
            }
          } else {
            orderArray("doing", "todo", task.idprocesstoken);
            await fromDoingTo_Done(task);
          }
        } else {
          orderArray("doing", "todo", task.idprocesstoken, destination.index);
          await fromDoingTo_Todo(task);
        }
      } else {
        orderArray("todo", "doing", task.idprocesstoken, destination.index);
        await fromTodoTo_Doing(task);
      }
    }
    setDragItem(null);
  };

  const [detail, setDetail] = useState<number | null>(null);
  const location = useLocation();

  useEffect(() => {
    const taskId = (location.state as { taskId?: number })?.taskId;
    if (taskId) {
      setDetail(taskId);
    }
  }, [location.state]);

  const totalActiveProcesses = useMemo(() => {
    return tasks.todo.length + tasks.doing.length;
  }, [tasks])

  useEffect(() => {
    refreshSubscriptionFeatures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalActiveProcesses]);

  const processedToDoTasks = useMemo(
    () =>
      processTasks(
        tasks.todo,
        searchText,
        searchTags,
        selectedPriorities,
        selectedActivities,
        selectedUserDataKey,
        selectedModels,
        sortCriteria,
        priorities
      )
        //commented atm
        // .filter(
        //   ({ idprocesstoken }) => idprocesstoken !== dragItem?.idprocesstoken
        // )
        .filter((task) => !showOnlyIdUserZero || task.iduser === 0)
        .filter(
          (task) =>
            selectedActivities.length === 0 ||
            selectedActivities.includes(task?.flownodename ?? "")
        ),
    [
      //dragitem dependency is related to the commented filter
      // dragItem,
      tasks.todo,
      searchText,
      searchTags,
      selectedPriorities,
      selectedUserDataKey,
      selectedModels,
      sortCriteria,
      priorities,
      selectedActivities,
      showOnlyIdUserZero,
    ]
  );
  const processedDoingTasks = useMemo(
    () =>
      processTasks(
        tasks.doing,
        searchText,
        searchTags,
        selectedPriorities,
        selectedActivities,
        selectedUserDataKey,
        selectedModels,
        sortCriteria,
        priorities
      )
        //commentend atm
        // .filter(
        //   ({ idprocesstoken }) => idprocesstoken !== dragItem?.idprocesstoken
        // )
        .filter(
          (task) =>
            selectedActivities.length === 0 ||
            selectedActivities.includes(task?.flownodename ?? "")
        ),
    [
      //dragitem dependency is related to the commented filter
      // dragItem,
      tasks.doing,
      searchText,
      searchTags,
      selectedPriorities,
      selectedUserDataKey,
      selectedModels,
      sortCriteria,
      priorities,
      selectedActivities,
      //   showOnlyIdUserZero
    ]
  );
  const appliedFilter = useMemo(() => {
    return (
      searchTags.length +
      (searchText ? 1 : 0) +
      selectedModels.length +
      selectedPriorities.length +
      selectedActivities.length
    );
  }, [
    selectedUserDataKey,
    searchTags,
    searchText,
    selectedModels,
    selectedPriorities,
    selectedActivities,
  ]);

  const displayedTags = useMemo(() => {
    return showAllTags ? filteredTags : filteredTags.slice(0, 5);
  }, [filteredTags, showAllTags]);

  const selectedTask = useMemo(() => {
    return (
      tasks.todo.find((_) => _.idprocesstoken === detail) ||
      tasks.doing.find((_) => _.idprocesstoken === detail)
    );
  }, [tasks, detail]);

  const handleClearFilters = useCallback(
    (disableLocalStorage = false, clearUserData = false) => {
      setSearchText("");
      setSearchTags([]);
      setSelectedPriorities([]);
      setSelectedModels([]);
      if (clearUserData) {
        setSelectedUserDataKey(null);
      }
      setSelectedActivities([]);
      if (!disableLocalStorage) {
        localStorage.removeItem(`${LOCAL_STORAGE_KEY}-${organization?.id}`);
      }
    },
    [organization, selectedUserDataKey, sortCriteria]
  );

  //anche questo sarà da rivedere per usarne solo uno (vedi ProcessStarted)
  useEffect(() => {
    return () => handleClearFilters(true);
  }, [handleClearFilters]);
  const setLoadingToTask = (task: Task) => {
    setTasks((__tasks) => {
      const _tasks = { ...__tasks };
      const container = task.state === 0 ? "todo" : "doing";
      // const sorted = sortTasks(
      //   _tasks[container].filter(
      //     filterTasks(
      //       searchText,
      //       searchTags,
      //       selectedPriorities,
      //       selectedActivities,
      //       selectedUserDataKey,
      //       selectedModels
      //     )
      //   ),
      //   sortCriteria,
      //   selectedUserDataKey,
      //   priorities
      // );
      const selectedIndex = _tasks[container].findIndex(
        (a) => a.idprocesstoken === task.idprocesstoken
      );
      _tasks[container][selectedIndex].loading = true;
      // _tasks[container] = sorted;
      return _tasks;
    });
  };
  if (organization && organization.id && user) {
    return (
      <React.Fragment>
        <TaskDetailDialog
          doing={selectedTask?.state === 1}
          task={selectedTask}
          open={Boolean(selectedTask)}
          onClose={() => setDetail(null)}
          handleNextStep={(task) => {
            setLoadingToTask(task);
            return selectedTask?.state === 1
              ? fromDoingTo_Done(task)
              : fromTodoTo_Doing(task);
          }}
          handleSequenceFlowChoice={(task, sequenceFlow) => {
            setLoadingToTask(task);
            return selectedTask?.state === 1
              ? fromDoingTo_Sequence(task, sequenceFlow)
              : undefined;
          }}
          handleBackStep={(task) => {
            setLoadingToTask(task);
            return selectedTask?.state === 1
              ? fromDoingTo_Todo(task)
              : undefined;
          }}
          administrator={organization.administrator}
          onPriorityUpdate={updateTaskPriority}
        />

        <StartProcess
          isAdmin={organization.administrator}
          open={startProcess}
          setOpen={setStartProcess}
        />
        <DragDropContext
          onDragUpdate={handleDragUpdate}
          onDragEnd={handleDragEnd}
          onBeforeDragStart={(e) => {
            const sorted = sortTasks(
              tasks[e.source.droppableId as "todo" | "doing"].filter(
                filterTasks(
                  searchText,
                  searchTags,
                  selectedPriorities,
                  selectedActivities,
                  selectedUserDataKey,
                  selectedModels
                )
              ),
              sortCriteria,
              selectedUserDataKey,
              priorities
            );
            const selected = sorted[e.source.index];
            setDragItem(selected);
          }}
        >
          <Stack sx={{ overflowX: "auto" }} height="100%" spacing={1}>
            <Stack
              sx={{ overflow: "hidden", width: 'fit-content' }}
              overflow="auto"
              flex={1}
              spacing={2}
            >
              <MyActivitiesFuncBar
                liveProcessCanUse={liveProcessCanUse}
                bypassSubscription={bypassSubscription}
                organization={organization}
                translations={translations}
                view={view}
                completeLabel={completeLabel}
                appliedFilter={appliedFilter}
                sortCriteria={sortCriteria}
                setStartProcess={setStartProcess}
                navigate={navigate}
                handleViewChange={handleViewChange}
                setCompleteLabel={setCompleteLabel}
                searchText={searchText}
                setSearchText={setSearchText}
                searchTags={searchTags}
                setSearchTags={setSearchTags}
                selectedPriorities={selectedPriorities}
                setSelectedPriorities={setSelectedPriorities}
                selectedModels={selectedModels}
                setSelectedModels={setSelectedModels}
                selectedActivities={selectedActivities}
                setSelectedActivities={setSelectedActivities}
                selectedUserDataKey={selectedUserDataKey}
                handleSelectUserDataKey={handleSelectUserDataKey}
                setSortCriteria={setSortCriteria}
                handleClearFilters={handleClearFilters}
                processModels={processModels}
                tasks={tasks}
                uniqueActivities={uniqueActivities}
                displayedTags={displayedTags}
                filteredTags={filteredTags}
                showAllTags={showAllTags}
                setShowAllTags={setShowAllTags}
                priorities={priorities}
                userDataKeys={userDataKeys}
                SUPPORTED_SORTS={SUPPORTED_SORTS}
              />
              <MyActivitiesPanels
                loading={loading}
                completeLabel={completeLabel}
                setDetail={setDetail}
                fromTodoTo_Doing={fromTodoTo_Doing}
                dragItem={dragItem}
                userId={user.userId}
                switchChecked={switchChecked}
                administrator={!!organization.administrator}
                processedToDoTasks={processedToDoTasks}
                showOnlyIdUserZero={showOnlyIdUserZero}
                setShowOnlyIdUserZero={setShowOnlyIdUserZero}
                view={view}
                fromDoingTo_Done={fromDoingTo_Done}
                fromDoingTo_Todo={fromDoingTo_Todo}
                fromDoingTo_Sequence={fromDoingTo_Sequence}
                processedDoingTasks={processedDoingTasks}
                all={all}
              />
            </Stack>
          </Stack>
        </DragDropContext>
        {sequenceTask && (
          <SequenceFlowsDialog
            gatewayQuestion={sequenceTask?.flownodename}
            open={openSequenceDialog}
            onClose={() => {
              setOpenSequenceDialog(false);
              setSequenceTask(null);
              setDragItem(null);
            }}
            sequenceFlows={sequenceTask?.sequenceflows ?? []}
            onSelect={(sequenceFlow: SequenceFlow) => {
              if (sequenceTask) {
                fromDoingTo_Sequence(sequenceTask, sequenceFlow);
                setOpenSequenceDialog(false);
                setSequenceTask(null);
                setDragItem(null);
              }
            }}
          />
        )}
      </React.Fragment>
    );
  } else {
    return (
      <Container
        sx={{
          display: "flex",
          JustifyContent: "center",
          alignItems: "center",
        }}
      >
        {getTranslation(translations, "placeholders.organization.select.label")}
      </Container>
    );
  }
};

export default MyActivities;
